import { Dispatch } from "@reduxjs/toolkit";
import { Modal } from "antd";
import { downloadData } from "aws-amplify/storage";
import { callNotify } from "../../../..";
import { useTrackEvent } from "../../../../components/AnalyticsProvider/useTrackEvent";
import { openPopup } from "../../../../components/Popup/_components/WrapperPopup";
import modal from "../../../../components/Popup/ModalCustom";
import PopupEditDuration from "../../../../components/Popup/PopupEditDuration";
import { GlobalService } from "../../../../util/services/global";
import { OpService } from "../../../../util/services/operation";
import { VesselSideService } from "../../../../util/services/vessel-side";
import { getParentEl, OP_STATUS, OPERATION_CONFIRM_STATUS } from "../../../../util/varibles/constants";
import { changeLoadingOp, downloadBlob, getElById, showErrorResponse } from "../../../../util/varibles/global";
import { IOperation, IOperations } from "../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../util/varibles/message";
import { loginActions } from "../../../LoginPage/reducer";
import { overviewActions } from "../../../OverviewPage/reducer";
import { SUPPORT_TYPE } from "../../constants";
import PopupAddDelay from "../../Popup/AddDelay";
import PopupSendMail from "../../Popup/SendMail";
import { planOpActions } from "../../reducer";
import { VALIDATION_STATUS } from "../../util/validation";

export const handleAddDelay = (data: any, dispatch: Dispatch) => {
    const opInfoEl = openPopup(<PopupAddDelay
        visible={true}
        data={data}
        onClose={() => setTimeout(() => opInfoEl.remove(), 200)}
        onSave={(payload: any) => dispatch(planOpActions.updateOpsByWs(payload))}
    />);
}

const includeEditedInvoiceOperations = (sendList: IOperation[], data: IOperation, operations: IOperations) => {
    const invoiceOperationIds = new Set(
        (data.operation.invoices || []).map(invoice => invoice.operation_id)
    );
    const editedInvoiceOperations = Object.values(operations).filter(item => {
        return invoiceOperationIds.has(item.operation.id)
            && item.action_type === OPERATION_CONFIRM_STATUS.UPDATE;
    });

    return Array.from(
        new Map([...sendList, ...editedInvoiceOperations].map(item => [item.operation.id, item])).values()
    );
};

export const handleSendToVessel = (data: any, operations: IOperations, dispatch: Dispatch, trackEvent?: ReturnType<typeof useTrackEvent>) => {
    let sendList = [];
    if (data.status === VALIDATION_STATUS.ERROR) {
        callNotify?.error({
            title: 'Error',
            description: `#${data.operation.operation_code}: ${data.message}`
        })
    } else {
        const { support: targetSupport, id: targetId } = data.operation;
        if (targetSupport && targetSupport.type === SUPPORT_TYPE.MAIN) {
            const { related_id } = targetSupport;
            sendList = Object.keys(operations).reduce((list: any, key) => {
                const item: any = operations[key];
                const { support } = item.operation;
                if (support && `${support.related_id}` === `${related_id}`)
                    return [...list, item]
                const { pusher = {} } = data;
                if (pusher[targetId])
                    return [...list, item];
                return list;
            }, []);
        } else {
            sendList = Object.keys(operations).reduce((list: any, key) => {
                const item: any = operations[key];
                const { pusher = {}, action_type } = item;
                if (pusher[targetId] && action_type)
                    return [...list, item];
                return list;
            }, [data]);
        }
        sendList = includeEditedInvoiceOperations(sendList, data, operations);
        dispatch(planOpActions.confirmOpRequest({ operations: sendList, isSave: false }));
        sendList.forEach((operation: IOperation) => trackEvent?.(
            'operation_planned', 'Plan Operation', { operation }
        ));
    }
}

export const handleSaveOperation = (data: any, operations: IOperations, dispatch: Dispatch, trackEvent?: ReturnType<typeof useTrackEvent>) => {
    let sendList = [];
    if (data.status === 2) {
        callNotify?.error({
            title: 'Error',
            description: `#${data.operation.operation_code}: ${data.message}`
        })
    } else {
        const { support: targetSupport, id: targetId } = data.operation;
        if (targetSupport && targetSupport.type === SUPPORT_TYPE.MAIN) {
            const { related_id } = targetSupport;
            sendList = Object.keys(operations).reduce((list: any, key) => {
                const item: any = operations[key];
                const { support } = item.operation;
                if (support && `${support.related_id}` === `${related_id}`)
                    return [...list, item]
                const { pusher = {} } = data;
                if (pusher[targetId])
                    return [...list, item];
                return list;
            }, []);
        } else {
            sendList = Object.keys(operations).reduce((list: any, key) => {
                const item: any = operations[key];
                const { pusher = {}, action_type } = item;
                if (pusher[targetId] && action_type)
                    return [...list, item];
                return list;
            }, [data]);
        }
        sendList = includeEditedInvoiceOperations(sendList, data, operations);
        dispatch(planOpActions.confirmOpRequest({ operations: sendList, isSave: true }));
        sendList.forEach((operation: IOperation) => trackEvent?.(
            'operation_planned', 'Plan Operation', { operation }
        ));
    }
}

export const handleCancelOperation = (data: IOperation, dispatch: Dispatch) => {
    const { id, tenant_id = '' } = data.operation;

    const params = {
        operation_id: id,
        tenant_id,
        status: OP_STATUS.CANCELED
    }

    modal.confirm({
        content: 'Are you sure you want to cancel this operation?',
        ok: {
            click: () => {
                OpService.updateStatus(params)
                    .then(() => {
                        callNotify?.success({ title: 'Success', description: 'Cancel successfully' })
                        afterChangeState.plan(data, { ...data, operation: params }, dispatch);
                    })
                    .catch((error) => {
                        showErrorResponse('Cancel failed', error);
                    })
            }
        }
    })
}

export const handleVesselCancelOp = (data: IOperation, dispatch: Dispatch) => {
    const { id, tenant_id, operation_code } = data.operation;

    const params = {
        operation_id: id,
        tenant_id,
        status: OP_STATUS.CANCELED
    }

    const modal = Modal.confirm({
        icon: null,
        getContainer: getParentEl,
        content: <div className='font-w4 font-s16'>Are you sure you want to cancel this operation?</div>,
        mask: { closable: true, },
        centered: true,
        okText: 'Confirm',
        width: 450,
        onOk: () => {
            modal.destroy();
            const el = getElById(id + '-loading');
            changeLoadingOp(el, true);
            VesselSideService.updateStatus(params)
                .then(rs => {
                    notify.success(NotifyCode.S8)([`#${operation_code}`])
                    changeLoadingOp(el, false);
                    afterChangeState.vessel(data, rs, dispatch);
                })
                .catch((error) => {
                    changeLoadingOp(el, false);
                    showErrorResponse(`#${operation_code} canceled failed`, error);
                })
        },
        okButtonProps: {
            danger: true,
            className: 'font-w4 bt-danger'
        },
        cancelButtonProps: {
            className: 'font-w4 bt-default'
        }
    })
}

export const handleDeclineOperation = (data: IOperation, dispatch: Dispatch) => {
    const { id, tenant_id = '' } = data.operation;

    const params = {
        operation_id: id,
        tenant_id,
        status: OP_STATUS.CANCELED
    }

    modal.confirm({
        content: 'Are you sure you want to decline this operation?',
        ok: {
            click: () => {
                OpService.updateStatus(params)
                    .then(() => {
                        callNotify?.success({ title: 'Success', description: 'Decline successfully' })
                        afterChangeState.plan(data, { ...data, operation: params }, dispatch);
                    })
                    .catch((error) => {
                        showErrorResponse('Decline failed', error);
                    })
            }
        }
    })
}

export const showPopupDeleteOperation = (data: IOperation, props: any, action: (ids: string[]) => void) => {

    const { id, operation_code, status } = data.operation;
    if (status === OP_STATUS.NEW && data.action_type === OPERATION_CONFIRM_STATUS.UPDATE) {
        props.dispatch(planOpActions.deleteCancelOp(id));
    } else if (data.action_type === OPERATION_CONFIRM_STATUS.NEW) {
        deleteSuccess([id], [operation_code], action);
    } else {
        modal.confirm({
            content: 'Are you sure you want to delete this operation?',
            subContent: 'Note: Delete this operation may cause duration changing on next operation (if have any) due to vessel’s start position.',
            ok: {
                click: () => {
                    const loadingEl = document.getElementById(id + '-loading');
                    changeLoadingOp(loadingEl, true);
                    OpService.delete(id)
                        .then(({ deleted_operations = [] }) => {
                            changeLoadingOp(loadingEl, false);
                            deleteSuccess(deleted_operations, [operation_code], action);
                        })
                        .catch((error) => {
                            changeLoadingOp(loadingEl, false);

                            if (error.response && error.response.status.toString() === '445') {
                                deleteSuccess([id], [operation_code], action);
                            } else
                                showErrorResponse('Delete failed', error);
                        })
                }
            }
        })
    }
};

export const deleteSuccess = (ids: any, code: any, action: (ids: string[]) => void) => {
    notify.success(code.length > 1 ? NotifyCode.S5 : NotifyCode.S4, "Delete successful")([code.join(', ')])
    action(ids);
}

export const openPopupDuration = (data: IOperation, dispatch: Dispatch) => {
    const opInfoEl = openPopup(<PopupEditDuration
        data={data}
        onSave={(op) => {
            dispatch(planOpActions.updateOps({ update: { [op.operation.id]: op } }))
        }}
        onClose={() => opInfoEl.remove()}
    />);
}

export const handleApproveOp = (data: IOperation, dispatch: Dispatch) => {
    const { id, tenant_id = '', operation_code } = data.operation;
    const params = {
        operation_id: id,
        tenant_id,
        status: OP_STATUS.WAITING
    }
    const el = getElById(id + '-loading');
    changeLoadingOp(el, true);
    OpService.updateStatus(params)
        .then((rs) => {
            notify.success(NotifyCode.S7)([`#${operation_code}`])
            changeLoadingOp(el, false);
            afterChangeState.plan(data, rs, dispatch);
        })
        .catch(error => {
            showErrorResponse(`#${operation_code} approved failed`, error)
            changeLoadingOp(el, false);
        })
}

export const handleVesselApproveOp = (data: IOperation, dispatch: Dispatch) => {
    const { id, tenant_id = '', operation_code } = data.operation;
    const params = {
        operation_id: id,
        tenant_id,
        status: OP_STATUS.APPROVED
    }
    const el = getElById(id + '-loading');
    changeLoadingOp(el, true);
    VesselSideService.updateStatus(params)
        .then((rs) => {
            notify.success(NotifyCode.S7)([`#${operation_code}`])
            changeLoadingOp(el, false);
            afterChangeState.vessel(data, rs, dispatch);
        })
        .catch(error => {
            showErrorResponse(`#${operation_code} approved failed`, error)
            changeLoadingOp(el, false);
        })
}

export const afterChangeState = {
    plan: (data: IOperation, rs: any, dispatch: Dispatch) => {
        const { activity_log } = rs;
        const { status, current_process } = rs.operation;
        dispatch(planOpActions.updateOpsByWs({
            data: [{
                ...data,
                operation: { ...data.operation, status, current_process },
                activity_log
            }],
        }))
    },
    vessel: (data: IOperation, rs: any, dispatch: Dispatch) => {
        const { activity_log } = rs;
        const { status, current_process, checklists } = rs.operation;
        const ops = [{
            ...data,
            operation: { ...data.operation, status, current_process, checklists },
            activity_log
        }]
        dispatch(overviewActions.updateOps(ops));
        if (status === OP_STATUS.CANCELED) {
            dispatch(loginActions.deleteOpOfVessel([data.operation.id]));
        } else {
            dispatch(loginActions.updateOpsOfVessel(ops));
        }
    }
}

export const handleSendMail = (data: IOperation) => {
    const el = openPopup(<PopupSendMail data={data} onClose={() => el.remove()} />)
}

export const handleDownloadPdf = (data: IOperation) => {
    const body = {
        vessel_id: data.vessel.id,
        id: data.operation.id || '',
        tenant_id: data.operation.tenant_id || '',
        timezone: Math.floor(new Date().getTimezoneOffset() / 60) * -1
    }
    GlobalService.opToPdf(body)
        .then((filePath: any) => {
            const fileName = filePath.slice(filePath.lastIndexOf('/') + 1);
            downloadData({ path: `public/${filePath}` }).result
                .then(result => result.body.blob())
                .then(blob => {
                    downloadBlob(blob, fileName)
                    notify.success(NotifyCode.S14)();
                })
        })
        .catch((error) => {
            showErrorResponse('Download failed', error)
        })
}

export const handleStartOp = (data: IOperation, dispatch: Dispatch) => {
    const { id, tenant_id = '', operation_code, checklists_v2 = [], vessel_id } = data.operation;
    const el = getElById(id + '-loading');
    const body = {
        operation_id: id,
        tenant_id,
        vessel_id,
        checklists_v2
    };
    changeLoadingOp(el, true);
    VesselSideService.updateState(body)
        .then((rs: any) => {
            notify.success(NotifyCode.S6)([`#${operation_code}`])
            changeLoadingOp(el, false);
            afterChangeState.vessel(data, rs, dispatch);
        })
        .catch((error) => {
            showErrorResponse(`#${operation_code} started failed`, error)
            changeLoadingOp(el, false);
        })
}
